/**
 * @file Defines shared types for the centralized API client.
 * @summary These types ensure a consistent structure for API responses and errors,
 * promoting predictable and type-safe communication between the frontend and the
 * Supabase Edge Functions.
 */

/**
 * Represents a successful API response.
 * @template T - The type of the data payload.
 */
export type ApiSuccessResponse<T> = {
  data: T;
  error: null;
};

/**
 * Represents a failed API response.
 */
export type ApiErrorResponse = {
  data: null;
  error: {
    message: string;
    details?: unknown; // Additional error details, e.g., validation errors
  };
};

/**
 * A union type representing either a successful or failed API response.
 * This is the standard return type for the centralized API client.
 * @template T - The type of the data payload in a successful response.
 */
export type ApiResponse<T> = ApiSuccessResponse<T> | ApiErrorResponse;

/**
 * A custom error class for API-related exceptions.
 * This can be used to throw and catch API-specific errors within the application.
 */
export class ApiError extends Error {
  constructor(message: string, public details?: unknown) {
    super(message);
    this.name = "ApiError";
  }
}
